import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface LessonSuggestions {
  suggested_lessons?: string[];
  go_deeper?: string[];
  build_foundation?: string[];
  coaching_note?: string;
}

export const useLessonSuggestions = (topicId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['lesson-suggestions', topicId],
    queryFn: async (): Promise<LessonSuggestions | null> => {
      if (!topicId || !user) return null;
      
      // 1. Get last lesson (highest lesson_number with status='generated')
      const { data: lastLesson, error: lessonError } = await supabase
        .from('topic_lessons')
        .select('id, lesson_number')
        .eq('topic_id', topicId)
        .eq('user_id', user.id)
        .eq('status', 'generated')
        .order('lesson_number', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (lessonError) {
        console.error('Error fetching last lesson:', lessonError);
        return null;
      }
      
      if (!lastLesson) return null;
      
      // 2. Get next_steps slide
      const { data: nextStepsSlide, error: slideError } = await supabase
        .from('lesson_slides')
        .select('structured')
        .eq('lesson_id', lastLesson.id)
        .eq('slide_type', 'next_steps')
        .maybeSingle();
      
      if (slideError) {
        console.error('Error fetching next_steps slide:', slideError);
        return null;
      }
      
      return (nextStepsSlide?.structured as LessonSuggestions) || null;
    },
    enabled: !!topicId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

